/**
 * Unit and structure rendering.
 *
 * Four instanced layers stacked by render order: selection rings under the
 * sprites, sprite bodies, turrets, then health bars on top. All four are driven
 * from the same wasm-backed entity buffer in one pass, so adding a unit costs
 * four float writes rather than four scene-graph objects.
 */

import * as THREE from 'three/webgpu';
import { float, length, mix, positionGeometry, texture, uv, vec2, vec3 } from 'three/tsl';
import { QuadLayer, applyTeamTint, atlasUV, attr } from './instanced.js';
import { SPRITE_FACINGS, teamColor } from './art.js';
import {
  F_BUILD_FRAC,
  F_FACING,
  F_HP_FRAC,
  F_ID,
  F_KIND,
  F_OWNER,
  F_SELECTED,
  F_TURRET,
  F_X,
  F_Y,
  KIND_COUNT,
  KIND_TANK,
  isBuilding,
} from '../game/constants.js';

/** World-space size of a single-tile unit sprite. */
const UNIT_SPRITE_SCALE = 1.6;

function facingColumn(radians: number): number {
  const t = radians / (Math.PI * 2);
  return ((Math.round(t * SPRITE_FACINGS) % SPRITE_FACINGS) + SPRITE_FACINGS) % SPRITE_FACINGS;
}

/** Footprint of each kind, so structures draw at their real size. */
const KIND_SCALE = new Float32Array(KIND_COUNT);

export function setKindScales(footprints: Uint8Array): void {
  for (let k = 0; k < KIND_COUNT; k++) {
    KIND_SCALE[k] = isBuilding(k) ? footprints[k] * 1.15 : UNIT_SPRITE_SCALE;
  }
}

function spriteMaterial(atlas: THREE.Texture): THREE.NodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial();

  const pos = attr('aPos', 'vec2');
  const cell = attr('aCell', 'vec2'); // column (facing), row (kind)
  const team = attr('aTeam', 'vec3');
  const scaleFade = attr('aScaleFade', 'vec2'); // world scale, alpha

  material.positionNode = vec3(
    positionGeometry.x.mul(scaleFade.x).add(pos.x),
    positionGeometry.y.mul(scaleFade.x).sub(pos.y),
    float(0),
  );

  const texel = texture(atlas, atlasUV(cell.x, cell.y, float(SPRITE_FACINGS), float(KIND_COUNT)));
  const tinted = applyTeamTint(texel, team);
  material.colorNode = tinted.rgb;
  material.opacityNode = tinted.a.mul(scaleFade.y);
  material.transparent = true;
  material.depthWrite = false;
  material.depthTest = false;

  return material;
}

export class EntityLayer {
  readonly bodies: QuadLayer;
  readonly turrets: QuadLayer;
  readonly selection: QuadLayer;
  readonly healthBars: QuadLayer;

  constructor(unitAtlas: THREE.Texture, turretAtlas: THREE.Texture) {
    this.bodies = new QuadLayer({
      capacity: 512,
      attributes: { aPos: 2, aCell: 2, aTeam: 3, aScaleFade: 2 },
      material: spriteMaterial(unitAtlas),
      renderOrder: 20,
    });

    this.turrets = new QuadLayer({
      capacity: 256,
      attributes: { aPos: 2, aCell: 2, aTeam: 3, aScaleFade: 2 },
      material: spriteMaterial(turretAtlas),
      renderOrder: 21,
    });

    this.selection = new QuadLayer({
      capacity: 256,
      attributes: { aPos: 2, aScaleFade: 2, aTeam: 3 },
      material: selectionMaterial(),
      renderOrder: 10,
    });

    this.healthBars = new QuadLayer({
      capacity: 512,
      attributes: { aPos: 2, aSize: 2, aFrac: 1 },
      material: healthBarMaterial(),
      renderOrder: 30,
    });
  }

  addTo(scene: THREE.Scene): void {
    scene.add(this.selection.mesh, this.bodies.mesh, this.turrets.mesh, this.healthBars.mesh);
  }

  /**
   * Rebuilds every instanced buffer from one pass over the entity view.
   *
   * `entities` is a view straight onto wasm memory: `stride` floats per entity
   * laid out per `view.rs`. `prev` holds the previous tick's positions so the
   * renderer can interpolate; `alpha` is the fraction between the two ticks.
   */
  update(
    entities: Float32Array,
    prev: Map<number, [number, number]>,
    count: number,
    stride: number,
    alpha: number,
    showHealthAlways: boolean,
  ): void {
    this.bodies.reserve(count);
    this.turrets.reserve(count);
    this.selection.reserve(count);
    this.healthBars.reserve(count);

    const bPos = this.bodies.array('aPos');
    const bCell = this.bodies.array('aCell');
    const bTeam = this.bodies.array('aTeam');
    const bScale = this.bodies.array('aScaleFade');

    const tPos = this.turrets.array('aPos');
    const tCell = this.turrets.array('aCell');
    const tTeam = this.turrets.array('aTeam');
    const tScale = this.turrets.array('aScaleFade');

    const sPos = this.selection.array('aPos');
    const sScale = this.selection.array('aScaleFade');
    const sTeam = this.selection.array('aTeam');

    const hPos = this.healthBars.array('aPos');
    const hSize = this.healthBars.array('aSize');
    const hFrac = this.healthBars.array('aFrac');

    let nBody = 0;
    let nTurret = 0;
    let nSel = 0;
    let nBar = 0;

    for (let i = 0; i < count; i++) {
      const o = i * stride;
      const id = entities[o + F_ID];
      const kind = entities[o + F_KIND] | 0;
      const owner = entities[o + F_OWNER] | 0;
      const hp = entities[o + F_HP_FRAC];
      const built = entities[o + F_BUILD_FRAC];
      const selected = entities[o + F_SELECTED] > 0.5;

      // Interpolate toward this tick's position from where it was last tick,
      // so 20Hz simulation still renders smoothly at display refresh rate.
      const tx = entities[o + F_X];
      const ty = entities[o + F_Y];
      const p = prev.get(id);
      const x = p ? p[0] + (tx - p[0]) * alpha : tx;
      const y = p ? p[1] + (ty - p[1]) * alpha : ty;

      const scale = KIND_SCALE[kind] || UNIT_SPRITE_SCALE;
      const col = teamColor(owner);
      // A structure under construction fades in as it goes up.
      const fade = isBuilding(kind) ? 0.35 + 0.65 * built : 1;

      bPos[nBody * 2] = x;
      bPos[nBody * 2 + 1] = y;
      bCell[nBody * 2] = facingColumn(entities[o + F_FACING]);
      bCell[nBody * 2 + 1] = kind;
      bTeam[nBody * 3] = col.r;
      bTeam[nBody * 3 + 1] = col.g;
      bTeam[nBody * 3 + 2] = col.b;
      bScale[nBody * 2] = scale;
      bScale[nBody * 2 + 1] = fade;
      nBody++;

      if (kind === KIND_TANK) {
        tPos[nTurret * 2] = x;
        tPos[nTurret * 2 + 1] = y;
        tCell[nTurret * 2] = facingColumn(entities[o + F_TURRET]);
        tCell[nTurret * 2 + 1] = kind;
        tTeam[nTurret * 3] = col.r;
        tTeam[nTurret * 3 + 1] = col.g;
        tTeam[nTurret * 3 + 2] = col.b;
        tScale[nTurret * 2] = scale;
        tScale[nTurret * 2 + 1] = fade;
        nTurret++;
      }

      if (selected) {
        sPos[nSel * 2] = x;
        sPos[nSel * 2 + 1] = y;
        sScale[nSel * 2] = scale * 0.92;
        sScale[nSel * 2 + 1] = 1;
        sTeam[nSel * 3] = col.r;
        sTeam[nSel * 3 + 1] = col.g;
        sTeam[nSel * 3 + 2] = col.b;
        nSel++;
      }

      // Health bars would be visual noise on every unit at all times, so they
      // appear when damaged, when selected, or while the player holds Alt.
      if (hp < 0.999 || selected || showHealthAlways) {
        const w = Math.max(0.8, scale * 0.75);
        hPos[nBar * 2] = x;
        hPos[nBar * 2 + 1] = y - scale * 0.55;
        hSize[nBar * 2] = w;
        hSize[nBar * 2 + 1] = 0.16;
        hFrac[nBar] = hp;
        nBar++;
      }
    }

    this.bodies.commit(nBody);
    this.turrets.commit(nTurret);
    this.selection.commit(nSel);
    this.healthBars.commit(nBar);
  }

  dispose(): void {
    this.bodies.dispose();
    this.turrets.dispose();
    this.selection.dispose();
    this.healthBars.dispose();
  }
}

/** A hollow ring drawn under selected units, tinted to the owner's colour. */
function selectionMaterial(): THREE.NodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial();
  const pos = attr('aPos', 'vec2');
  const scaleFade = attr('aScaleFade', 'vec2');
  const team = attr('aTeam', 'vec3');

  material.positionNode = vec3(
    positionGeometry.x.mul(scaleFade.x).add(pos.x),
    positionGeometry.y.mul(scaleFade.x).sub(pos.y),
    float(0),
  );

  // Distance field from the quad centre gives a crisp ring at any zoom.
  const d = length(uv().sub(vec2(0.5, 0.5))).mul(2);
  const ring = d.smoothstep(0.72, 0.82).sub(d.smoothstep(0.94, 1.0));
  material.colorNode = team.add(vec3(0.35, 0.35, 0.35));
  material.opacityNode = ring.mul(scaleFade.y).mul(0.95);
  material.transparent = true;
  material.depthWrite = false;
  material.depthTest = false;
  return material;
}

/** Classic segmented RTS health bar: green through yellow to red. */
function healthBarMaterial(): THREE.NodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial();
  const pos = attr('aPos', 'vec2');
  const size = attr('aSize', 'vec2');
  const frac = attr('aFrac', 'float');

  material.positionNode = vec3(
    positionGeometry.x.mul(size.x).add(pos.x),
    positionGeometry.y.mul(size.y).sub(pos.y),
    float(0),
  );

  const filled = uv().x.lessThan(frac);
  const good = vec3(0.25, 0.85, 0.3);
  const warn = vec3(0.95, 0.8, 0.15);
  const bad = vec3(0.9, 0.2, 0.15);
  // Two-stage ramp so the colour change is legible at a glance.
  const health = mix(mix(bad, warn, frac.smoothstep(0.2, 0.5)), good, frac.smoothstep(0.5, 0.8));
  const border = uv().y.smoothstep(0.0, 0.12).mul(float(1).sub(uv().y.smoothstep(0.88, 1.0)));

  material.colorNode = filled.select(health, vec3(0.08, 0.08, 0.1)).mul(border.mul(0.4).add(0.6));
  material.opacityNode = float(0.92);
  material.transparent = true;
  material.depthWrite = false;
  material.depthTest = false;
  return material;
}
